import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";

import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

import MusicStore from "@/pages/MusicStore";
import TemplatesStore from "@/pages/TemplatesStore";
import EbooksStore from "@/pages/EbooksStore";
import MerchStore from "@/pages/MerchStore";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Downloads from "@/pages/Downloads";
import CustomerDashboard from "@/pages/CustomerDashboard";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSettings from "@/pages/AdminSettings";

// Church Builder (HOH Ministries Cloud) module
import ChurchBuilderLanding from "@/pages/churchbuilder/ChurchBuilderLanding";
import ChurchBuilderPricing from "@/pages/churchbuilder/ChurchBuilderPricing";
import ChurchBuilderTemplatesPage from "@/pages/churchbuilder/ChurchBuilderTemplatesPage";
import ChurchBuilderRegister from "@/pages/churchbuilder/ChurchBuilderRegister";
import ChurchBuilderDashboard from "@/pages/churchbuilder/ChurchBuilderDashboard";
import ChurchSlugDashboard from "@/pages/churchbuilder/ChurchSlugDashboard";
import ChurchBuilderAdmin from "@/pages/churchbuilder/ChurchBuilderAdmin";
import PublicChurchSite from "@/pages/churchbuilder/PublicChurchSite";
import ChurchSubscribe from "@/pages/churchbuilder/ChurchSubscribe";
import ChurchSubscribeSuccess from "@/pages/churchbuilder/ChurchSubscribeSuccess";
import PublicChurchDonate from "@/pages/churchbuilder/PublicChurchDonate";
import PublicChurchDonateSuccess from "@/pages/churchbuilder/PublicChurchDonateSuccess";
import PublicChurchPrayer from "@/pages/churchbuilder/PublicChurchPrayer";
import ResetPassword from "@/pages/ResetPassword";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AppLanding from "@/pages/AppLanding";
import MarketplaceHome from "@/pages/MarketplaceHome";
import AttendanceLanding from "@/pages/attendance/AttendanceLanding";
import AttendanceRegister from "@/pages/attendance/AttendanceRegister";
import AttendanceDashboard from "@/pages/attendance/AttendanceDashboard";
import AttendanceEvent from "@/pages/attendance/AttendanceEvent";
import UsherCounter from "@/pages/attendance/UsherCounter";
import UsherQrClaim from "@/pages/attendance/UsherQrClaim";
import ProjectionLanding from "@/pages/projection/ProjectionLanding";
import ProjectionConsole from "@/pages/projection/ProjectionConsole";
import ProjectionDisplay from "@/pages/projection/ProjectionDisplay";
import EducationComingSoon from "@/pages/EducationComingSoon";

// Public church sites are full-bleed (no HOH navbar / footer)
function PublicChurchRoute() {
  return <PublicChurchSite />;
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Routes>
            {/* Public church sites — no platform chrome */}
            <Route path="/churches/:slug" element={<PublicChurchRoute />} />
            <Route path="/attendance/u/:token" element={<UsherCounter />} />
            <Route path="/attendance/qr/:qr" element={<UsherQrClaim />} />
            <Route path="/projection/display" element={<ProjectionDisplay />} />

            {/* Everything else — wrapped in the standard HOH layout */}
            <Route path="*" element={
              <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
                <Navbar />
                <main className="flex-1">
                  <Routes>
                    <Route path="/" element={<MarketplaceHome />} />
                    <Route path="/music" element={<MusicStore />} />
                    <Route path="/templates" element={<TemplatesStore />} />
                    <Route path="/ebooks" element={<EbooksStore />} />
                    <Route path="/merch" element={<MerchStore />} />
                    <Route path="/product/:id" element={<ProductDetail />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout/success" element={<CheckoutSuccess />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/downloads" element={<Downloads />} />
                    <Route path="/dashboard" element={<CustomerDashboard />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/admin/login" element={<AdminLogin />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/settings" element={<AdminSettings />} />

                    {/* Church Builder module */}
                    <Route path="/church-builder" element={<ChurchBuilderLanding />} />
                    <Route path="/church-builder/pricing" element={<ChurchBuilderPricing />} />
                    <Route path="/church-builder/templates" element={<ChurchBuilderTemplatesPage />} />
                    <Route path="/church-builder/login" element={<Login />} />
                    <Route path="/church-builder/register" element={<ChurchBuilderRegister />} />
                    <Route path="/church-builder/dashboard" element={<ChurchBuilderDashboard />} />
                    <Route path="/church-builder/:slug/dashboard" element={<ChurchSlugDashboard />} />
                    <Route path="/church-builder/:slug/dashboard/billing" element={<ChurchSlugDashboard />} />
                    <Route path="/church-builder/:slug/subscribe" element={<ChurchSubscribe />} />
                    <Route path="/church-builder/:slug/subscribe/success" element={<ChurchSubscribeSuccess />} />
                    <Route path="/church-builder/admin" element={<ChurchBuilderAdmin />} />
                    <Route path="/churches/:slug/donate" element={<PublicChurchDonate />} />
                    <Route path="/churches/:slug/donate/success" element={<PublicChurchDonateSuccess />} />
                    <Route path="/churches/:slug/prayer" element={<PublicChurchPrayer />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/app" element={<AppLanding />} />
                    <Route path="/marketplace" element={<MarketplaceHome />} />
                    <Route path="/attendance" element={<AttendanceLanding />} />
                    <Route path="/attendance/register" element={<AttendanceRegister />} />
                    <Route path="/attendance/dashboard" element={<AttendanceDashboard />} />
                    <Route path="/attendance/events/:id" element={<AttendanceEvent />} />
                    <Route path="/projection" element={<ProjectionLanding />} />
                    <Route path="/projector" element={<Navigate to="/projection" replace />} />
                    <Route path="/education" element={<EducationComingSoon />} />
                    <Route path="/projection/register" element={<AttendanceRegister product="projection" />} />
                    <Route path="/projection/console" element={<ProjectionConsole />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            } />
          </Routes>
          <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#141414", border: "1px solid rgba(212,175,55,0.3)", color: "#FAFAFA" } }} />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
